import { createSlice, nanoid, PayloadAction } from '@reduxjs/toolkit'
import type { AppThunk, RootState } from '../../app/store'
import { patientsApi } from '../../services/patients'
import { Patient } from '../../types'
import ApiClient from '../../utils/api/ApiClient'
import { CreatePatientRequest } from '../../utils/patient/patientPayload'
import {
  EhrPatientResolution,
  EhrPatientUnavailableReason,
  ehrLaunchPatientApi,
} from './ehrLaunchPatientApi'
import {
  EhrAcquisitionOrigin,
  ehrLaunchEstablished,
  ehrPatientAcquisitionCleared,
  ehrPatientBound,
  ehrPatientInvalidated,
} from './ehrPatientAcquisitionActions'
import { ehrLaunchCoordination } from './ehrLaunchCoordination'

export type EhrPatientAcquisitionStatus =
  | 'inactive'
  | 'unresolved'
  | 'resolving'
  | 'selection_required'
  | 'registration_required'
  | 'resolved'
  | 'unavailable'
  | 'expired'
  | 'replaced'

export interface EhrPatientBinding {
  launchInstanceId: string
  patientId: string
}

interface EhrPatientAcquisitionState {
  status: EhrPatientAcquisitionStatus
  launchInstanceId?: string
  attemptId?: string
  binding?: EhrPatientBinding
  origin?: EhrAcquisitionOrigin
  candidates: Patient[]
  draft?: Partial<CreatePatientRequest>
  unavailableReason?: EhrPatientUnavailableReason
  retryable?: boolean
  registrationBusy: boolean
  registrationError?: string
  duplicateRecheckUsed: boolean
  duplicateRecheckInProgress: boolean
  focusVerificationInProgress: boolean
  focusVerificationRequired: boolean
  focusAttemptId?: string
  focusError?: string
}

const initialState: EhrPatientAcquisitionState = {
  status: 'inactive',
  candidates: [],
  registrationBusy: false,
  duplicateRecheckUsed: false,
  duplicateRecheckInProgress: false,
  focusVerificationInProgress: false,
  focusVerificationRequired: false,
}

const DUPLICATE_IHI_MESSAGE =
  'A patient with this IHI already exists. Review the details and try again.'

const clearTransient = (state: EhrPatientAcquisitionState) => {
  state.attemptId = undefined
  state.candidates = []
  state.draft = undefined
  state.unavailableReason = undefined
  state.retryable = undefined
  state.registrationBusy = false
  state.registrationError = undefined
  state.duplicateRecheckUsed = false
  state.duplicateRecheckInProgress = false
}

const clearFocus = (state: EhrPatientAcquisitionState) => {
  state.focusVerificationInProgress = false
  state.focusVerificationRequired = false
  state.focusAttemptId = undefined
  state.focusError = undefined
}

const currentAttempt = (state: RootState, launchInstanceId: string, attemptId: string) => {
  const acquisition = state.ehrPatientAcquisition
  return (
    state.authenticatedProvider === 'ehr' &&
    acquisition.launchInstanceId === launchInstanceId &&
    acquisition.attemptId === attemptId
  )
}

const currentFocusAttempt = (
  state: RootState,
  launchInstanceId: string,
  focusAttemptId: string
) => {
  const acquisition = state.ehrPatientAcquisition
  return (
    state.authenticatedProvider === 'ehr' &&
    acquisition.launchInstanceId === launchInstanceId &&
    acquisition.focusAttemptId === focusAttemptId
  )
}

const acquisitionSlice = createSlice({
  name: 'ehrPatientAcquisition',
  initialState,
  reducers: {
    resolutionStarted: (
      state,
      {
        payload,
      }: PayloadAction<{
        launchInstanceId: string
        attemptId: string
        origin: EhrAcquisitionOrigin
      }>
    ) => {
      if (state.launchInstanceId !== payload.launchInstanceId) return
      clearTransient(state)
      state.status = 'resolving'
      state.attemptId = payload.attemptId
      state.origin = payload.origin
      state.binding = undefined
    },
    selectionRequired: (
      state,
      {
        payload,
      }: PayloadAction<{ launchInstanceId: string; attemptId: string; candidates: Patient[] }>
    ) => {
      if (
        state.launchInstanceId !== payload.launchInstanceId ||
        state.attemptId !== payload.attemptId
      )
        return
      state.status = 'selection_required'
      state.candidates = payload.candidates
      state.registrationBusy = false
      state.duplicateRecheckInProgress = false
    },
    registrationRequired: (
      state,
      {
        payload,
      }: PayloadAction<{
        launchInstanceId: string
        attemptId: string
        draft?: Partial<CreatePatientRequest>
        preserveDraft?: boolean
        conflict?: boolean
      }>
    ) => {
      if (
        state.launchInstanceId !== payload.launchInstanceId ||
        state.attemptId !== payload.attemptId
      )
        return
      state.status = 'registration_required'
      if (!payload.preserveDraft) state.draft = payload.draft
      state.registrationBusy = false
      state.duplicateRecheckInProgress = false
      state.registrationError = payload.conflict ? DUPLICATE_IHI_MESSAGE : undefined
    },
    unavailable: (
      state,
      {
        payload,
      }: PayloadAction<{
        launchInstanceId: string
        attemptId: string
        reason: EhrPatientUnavailableReason
        retryable: boolean
      }>
    ) => {
      if (
        state.launchInstanceId !== payload.launchInstanceId ||
        state.attemptId !== payload.attemptId
      )
        return
      state.status = 'unavailable'
      state.unavailableReason = payload.reason
      state.retryable = payload.retryable
      state.binding = undefined
    },
    registrationStarted: (
      state,
      { payload }: PayloadAction<{ launchInstanceId: string; attemptId: string }>
    ) => {
      if (
        state.status !== 'registration_required' ||
        state.launchInstanceId !== payload.launchInstanceId ||
        state.attemptId !== payload.attemptId ||
        state.registrationBusy
      )
        return
      state.registrationBusy = true
      state.registrationError = undefined
    },
    registrationStopped: (
      state,
      { payload }: PayloadAction<{ launchInstanceId: string; attemptId: string; message: string }>
    ) => {
      if (
        state.launchInstanceId !== payload.launchInstanceId ||
        state.attemptId !== payload.attemptId
      )
        return
      state.registrationBusy = false
      state.duplicateRecheckInProgress = false
      state.registrationError = payload.message
    },
    duplicateRecheckStarted: (
      state,
      {
        payload,
      }: PayloadAction<{
        launchInstanceId: string
        previousAttemptId: string
        attemptId: string
      }>
    ) => {
      if (
        state.status !== 'registration_required' ||
        state.launchInstanceId !== payload.launchInstanceId ||
        state.attemptId !== payload.previousAttemptId ||
        state.duplicateRecheckUsed
      )
        return
      state.attemptId = payload.attemptId
      state.duplicateRecheckUsed = true
      state.duplicateRecheckInProgress = true
      state.registrationBusy = true
    },
    sessionExpired: (state) => {
      clearTransient(state)
      clearFocus(state)
      state.status = 'expired'
      state.binding = undefined
    },
    focusStarted: (
      state,
      { payload }: PayloadAction<{ launchInstanceId: string; focusAttemptId: string }>
    ) => {
      if (state.launchInstanceId !== payload.launchInstanceId || state.focusVerificationInProgress)
        return
      state.focusVerificationInProgress = true
      state.focusVerificationRequired = true
      state.focusAttemptId = payload.focusAttemptId
      state.focusError = undefined
    },
    focusVerified: (
      state,
      { payload }: PayloadAction<{ launchInstanceId: string; focusAttemptId: string }>
    ) => {
      if (
        state.launchInstanceId !== payload.launchInstanceId ||
        state.focusAttemptId !== payload.focusAttemptId
      )
        return
      clearFocus(state)
    },
    focusFailed: (
      state,
      {
        payload,
      }: PayloadAction<{ launchInstanceId: string; focusAttemptId: string; message: string }>
    ) => {
      if (
        state.launchInstanceId !== payload.launchInstanceId ||
        state.focusAttemptId !== payload.focusAttemptId
      )
        return
      state.focusVerificationInProgress = false
      state.focusVerificationRequired = true
      state.focusError = payload.message
    },
    routeLeft: (state) => {
      if (state.status === 'resolved') return
      if (
        state.status !== 'expired' &&
        state.status !== 'replaced' &&
        state.status !== 'inactive'
      ) {
        clearTransient(state)
        clearFocus(state)
        state.status = 'unresolved'
        state.origin = 'restarting'
        state.binding = undefined
      }
    },
    replaced: (state) => {
      clearTransient(state)
      clearFocus(state)
      state.status = 'replaced'
      state.binding = undefined
    },
    resetAcquisition: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(ehrLaunchEstablished, (state, { payload }) => {
        if (!payload.reset && state.launchInstanceId === payload.launchInstanceId) return
        clearTransient(state)
        clearFocus(state)
        state.status = 'unresolved'
        state.launchInstanceId = payload.launchInstanceId
        state.origin = payload.origin
        state.binding = undefined
      })
      .addCase(ehrPatientBound, (state, { payload }) => {
        if (state.launchInstanceId !== payload.launchInstanceId || !state.attemptId) return
        clearTransient(state)
        clearFocus(state)
        state.status = 'resolved'
        state.binding = {
          launchInstanceId: payload.launchInstanceId,
          patientId: payload.patient.id,
        }
      })
  },
})

const {
  resolutionStarted,
  selectionRequired,
  registrationRequired,
  unavailable,
  registrationStarted,
  registrationStopped,
  duplicateRecheckStarted,
  sessionExpired,
  focusStarted,
  focusVerified,
  focusFailed,
  routeLeft,
  replaced,
  resetAcquisition,
} = acquisitionSlice.actions

const httpStatus = (error: unknown) =>
  (error as { status?: number; response?: { status?: number } })?.response?.status ??
  (error as { status?: number })?.status

const failResolution =
  (error: unknown, launchInstanceId: string, attemptId: string): AppThunk =>
  (dispatch) => {
    if (httpStatus(error) === 401) {
      dispatch(sessionExpired())
      dispatch(ehrPatientInvalidated())
      return
    }
    dispatch(
      unavailable({
        launchInstanceId,
        attemptId,
        reason: 'upstream',
        retryable: httpStatus(error) !== 400 && httpStatus(error) !== 403,
      })
    )
    dispatch(ehrPatientInvalidated())
  }

const interpretResolution =
  (
    result: EhrPatientResolution,
    launchInstanceId: string,
    attemptId: string,
    duplicateRecheck = false
  ): AppThunk =>
  (dispatch) => {
    if (result.status === 'matched') {
      dispatch(ehrPatientBound({ launchInstanceId, patient: result.patient }))
      return
    }
    if (result.status === 'selection_required') {
      dispatch(selectionRequired({ launchInstanceId, attemptId, candidates: result.candidates }))
      return
    }
    if (result.status === 'registration_required') {
      dispatch(
        registrationRequired({
          launchInstanceId,
          attemptId,
          draft: result.draft,
          preserveDraft: duplicateRecheck,
          conflict: duplicateRecheck,
        })
      )
      return
    }
    if (duplicateRecheck) {
      dispatch(
        registrationStopped({
          launchInstanceId,
          attemptId,
          message: DUPLICATE_IHI_MESSAGE,
        })
      )
      return
    }
    dispatch(
      unavailable({
        launchInstanceId,
        attemptId,
        reason: result.reason,
        retryable: result.retryable,
      })
    )
    dispatch(ehrPatientInvalidated())
  }

export const establishEhrLaunch =
  (launchInstanceId: string, origin: EhrAcquisitionOrigin): AppThunk =>
  (dispatch, getState) => {
    const reset = getState().ehrPatientAcquisition.launchInstanceId !== launchInstanceId
    dispatch(ehrLaunchEstablished({ launchInstanceId, origin, reset }))
    ehrLaunchCoordination.publish(launchInstanceId)
  }

export const enterEhrNewScriptRoute = (): AppThunk<Promise<void>> => async (dispatch, getState) => {
  const state = getState().ehrPatientAcquisition
  if (
    getState().authenticatedProvider !== 'ehr' ||
    state.status !== 'unresolved' ||
    !state.launchInstanceId
  )
    return
  const launchInstanceId = state.launchInstanceId
  const attemptId = nanoid()
  dispatch(resolutionStarted({ launchInstanceId, attemptId, origin: state.origin ?? 'fresh' }))
  try {
    const result = await dispatch(
      ehrLaunchPatientApi.endpoints.resolveLaunchPatient.initiate()
    ).unwrap()
    if (!currentAttempt(getState(), launchInstanceId, attemptId)) return
    dispatch(interpretResolution(result, launchInstanceId, attemptId))
  } catch (error) {
    if (!currentAttempt(getState(), launchInstanceId, attemptId)) return
    dispatch(failResolution(error, launchInstanceId, attemptId))
  }
}

export const leaveEhrNewScriptRoute = (): AppThunk => (dispatch) => dispatch(routeLeft())

export const retryEhrPatientAcquisition =
  (): AppThunk<Promise<void>> => async (dispatch, getState) => {
    const state = getState().ehrPatientAcquisition
    if (state.status !== 'unavailable' || !state.retryable || !state.launchInstanceId) return
    dispatch(
      ehrLaunchEstablished({
        launchInstanceId: state.launchInstanceId,
        origin: 'restarting',
        reset: true,
      })
    )
    await dispatch(enterEhrNewScriptRoute())
  }

export const chooseEhrPatient =
  (patientId: string): AppThunk =>
  (dispatch, getState) => {
    const state = getState().ehrPatientAcquisition
    if (
      state.status !== 'selection_required' ||
      !state.launchInstanceId ||
      !state.attemptId ||
      state.focusVerificationRequired
    )
      return
    const patient = state.candidates.find((candidate) => candidate.id === patientId)
    if (!patient || !currentAttempt(getState(), state.launchInstanceId, state.attemptId)) return
    dispatch(ehrPatientBound({ launchInstanceId: state.launchInstanceId, patient }))
  }

const isDuplicateIhiConflict = (error: unknown) => {
  const data = (error as { data?: { error?: string } })?.data
  return httpStatus(error) === 409 && data?.error === 'duplicated-ihi-number'
}

const errorMessage = (error: unknown) => {
  const data = (error as { data?: { message?: string } })?.data
  return data?.message ?? 'The patient could not be created. Review the details and try again.'
}

const recheckAfterDuplicate =
  (launchInstanceId: string, previousAttemptId: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const attemptId = nanoid()
    dispatch(duplicateRecheckStarted({ launchInstanceId, previousAttemptId, attemptId }))
    if (!currentAttempt(getState(), launchInstanceId, attemptId)) return
    try {
      const result = await dispatch(
        ehrLaunchPatientApi.endpoints.resolveLaunchPatient.initiate()
      ).unwrap()
      if (!currentAttempt(getState(), launchInstanceId, attemptId)) return
      dispatch(interpretResolution(result, launchInstanceId, attemptId, true))
    } catch {
      if (!currentAttempt(getState(), launchInstanceId, attemptId)) return
      dispatch(
        registrationStopped({
          launchInstanceId,
          attemptId,
          message: DUPLICATE_IHI_MESSAGE,
        })
      )
    }
  }

export const registerEhrPatient =
  (patient: CreatePatientRequest): AppThunk<Promise<Patient | undefined>> =>
  async (dispatch, getState) => {
    const state = getState().ehrPatientAcquisition
    if (
      state.status !== 'registration_required' ||
      !state.launchInstanceId ||
      !state.attemptId ||
      state.registrationBusy ||
      state.focusVerificationRequired
    )
      return undefined
    const { launchInstanceId, attemptId, duplicateRecheckUsed } = state
    dispatch(registrationStarted({ launchInstanceId, attemptId }))
    try {
      const registered = await dispatch(
        patientsApi.endpoints.createPatient.initiate({ patient })
      ).unwrap()
      if (!currentAttempt(getState(), launchInstanceId, attemptId)) return undefined
      dispatch(ehrPatientBound({ launchInstanceId, patient: registered }))
      return registered
    } catch (error) {
      if (!currentAttempt(getState(), launchInstanceId, attemptId)) return undefined
      if (httpStatus(error) === 401) {
        dispatch(expireEhrSession())
        return undefined
      }
      if (isDuplicateIhiConflict(error) && !duplicateRecheckUsed) {
        await dispatch(recheckAfterDuplicate(launchInstanceId, attemptId))
        return undefined
      }
      dispatch(
        registrationStopped({
          launchInstanceId,
          attemptId,
          message: errorMessage(error),
        })
      )
      return undefined
    }
  }

export const expireEhrSession = (): AppThunk => (dispatch, getState) => {
  if (getState().authenticatedProvider !== 'ehr') return
  dispatch(sessionExpired())
  dispatch(ehrPatientInvalidated())
}

export const replaceEhrLaunch = (): AppThunk => (dispatch, getState) => {
  if (getState().authenticatedProvider !== 'ehr') return
  dispatch(replaced())
  dispatch(ehrPatientInvalidated())
}

export const observeEhrLaunch =
  (launchInstanceId: string): AppThunk =>
  (dispatch, getState) => {
    const current = getState().ehrPatientAcquisition.launchInstanceId
    if (!current || current === launchInstanceId) return
    dispatch(replaceEhrLaunch())
  }

export const verifyActiveEhrLaunch = (): AppThunk<Promise<void>> => async (dispatch, getState) => {
  const state = getState().ehrPatientAcquisition
  if (
    getState().authenticatedProvider !== 'ehr' ||
    !state.launchInstanceId ||
    state.status === 'inactive' ||
    state.status === 'expired' ||
    state.status === 'replaced' ||
    state.focusVerificationInProgress
  )
    return
  const launchInstanceId = state.launchInstanceId
  const focusAttemptId = nanoid()
  dispatch(focusStarted({ launchInstanceId, focusAttemptId }))
  try {
    const response = await ApiClient({ url: '/auth/ehr-launch/bootstrap', method: 'post' })
    if (!currentFocusAttempt(getState(), launchInstanceId, focusAttemptId)) return
    const activeLaunchId = (response.data as { launchInstanceId?: string }).launchInstanceId
    if (activeLaunchId !== launchInstanceId) {
      dispatch(replaceEhrLaunch())
      return
    }
    dispatch(focusVerified({ launchInstanceId, focusAttemptId }))
    if (getState().ehrPatientAcquisition.status === 'unresolved') {
      await dispatch(enterEhrNewScriptRoute())
    }
  } catch (error) {
    if (!currentFocusAttempt(getState(), launchInstanceId, focusAttemptId)) return
    if (httpStatus(error) === 401) {
      dispatch(expireEhrSession())
      return
    }
    dispatch(
      focusFailed({
        launchInstanceId,
        focusAttemptId,
        message: 'RxPad could not verify this EHR launch. Check again before continuing.',
      })
    )
  }
}

export const clearEhrPatientAcquisition = (): AppThunk => (dispatch, getState) => {
  if (getState().authenticatedProvider !== 'ehr') return
  dispatch(resetAcquisition())
  dispatch(ehrPatientAcquisitionCleared())
}

export const selectIsEhrPrescribingAuthorized = (state: RootState) => {
  const acquisition = state.ehrPatientAcquisition
  const binding = acquisition.binding
  return (
    acquisition.status === 'resolved' &&
    !acquisition.focusVerificationRequired &&
    binding?.launchInstanceId === acquisition.launchInstanceId &&
    binding?.patientId === state.patientDetail.selectedPatient.id
  )
}

export const isPrescribingTargetAllowed = (
  provider: RootState['authenticatedProvider'],
  isEhrPrescribingAuthorized: boolean,
  ehrBoundPatientId: string | undefined,
  patientId: string
) => provider !== 'ehr' || (isEhrPrescribingAuthorized && ehrBoundPatientId === patientId)

export const selectCanPrescribeFor = (state: RootState, patientId: string) =>
  isPrescribingTargetAllowed(
    state.authenticatedProvider,
    selectIsEhrPrescribingAuthorized(state),
    state.ehrPatientAcquisition.binding?.patientId,
    patientId
  )

export default acquisitionSlice.reducer
